import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { firm } from "@/config/firm.config";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const EFFECTIVE_DATE = "12 September 2026";

const SECTIONS = [
  {
    n: "01",
    h: "Information We Collect",
    b: "We may collect information voluntarily provided by you, including your name, contact details, correspondence, case-related information, documents and other information necessary for providing legal services. We may also collect limited technical information when you visit our website, such as IP address, browser type, and website usage data.",
  },
  {
    n: "02",
    h: "Use of Information",
    b: "Information collected by us may be used to:",
    list: [
      "provide legal advice and professional services",
      "respond to enquiries and consultation requests",
      "communicate with clients and prospective clients",
      "conduct conflict checks and comply with professional and legal obligations",
      "maintain case and administrative records",
      "improve the security and functioning of our website and services",
    ],
  },
  {
    n: "03",
    h: "Confidentiality",
    b: "Information shared with us in connection with legal services is treated with appropriate confidentiality and professional care, subject to applicable law, professional obligations and circumstances where disclosure is legally required or otherwise permitted.",
  },
  {
    n: "04",
    h: "Sharing of Information",
    b: "We do not sell or commercially trade personal information. Information may be shared with employees, professional associates, service providers, authorities, courts, tribunals or other persons where reasonably necessary for providing legal services, complying with legal obligations, or protecting our legitimate interests.",
  },
  {
    n: "05",
    h: "Data Security",
    b: "We take reasonable technical and organisational measures to protect personal information against unauthorised access, disclosure, alteration, loss or misuse. However, no method of electronic storage or transmission can be guaranteed to be completely secure.",
  },
  {
    n: "06",
    h: "Retention of Information",
    b: "Personal information and case records may be retained for as long as reasonably necessary for providing services, maintaining professional records, complying with applicable legal and regulatory requirements or protecting our legal interests.",
  },
  {
    n: "07",
    h: "Cookies and Website Data",
    b: "Our website may use cookies or similar technologies to improve functionality, security and user experience. You may adjust your browser settings to manage or disable cookies, although certain website features may be affected.",
  },
  {
    n: "08",
    h: "Third-Party Websites",
    b: "Our website may contain links to third-party websites. We are not responsible for the privacy practices, content or security of such external websites. We encourage users to review their respective privacy policies.",
  },
  {
    n: "09",
    h: "Your Rights",
    b: "Subject to applicable law, you may have rights relating to access, correction, updating or other lawful handling of your personal information. Requests concerning personal information may be directed to us using the contact details provided below.",
  },
  {
    n: "10",
    h: "Changes to This Policy",
    b: "We may update this Privacy Policy from time to time to reflect changes in our services, legal requirements or privacy practices. The updated policy will be published on our website with the revised effective date.",
  },
  {
    n: "11",
    h: "Contact Us",
    b: "For questions, concerns, or requests regarding this Privacy Policy or the handling of personal information, please contact us any time through this website.",
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Privacy Policy — ${firm.name}`;
  }, []);

  return (
    <div className="min-h-screen bg-navy-900 text-paper" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b border-gold/20 bg-navy-900/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="privacy-home-logo" aria-label={`${firm.name} — home`} className="focus-ring">
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="focus-ring inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:text-gold-light"
          >
            <ArrowLeft size={14} /> Back to Site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(700px 380px at 20% 10%, rgba(224,184,120,0.14), transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Legal</p>
            <h1 className="mt-6 font-serif text-4xl font-light tracking-tight text-paper sm:text-5xl lg:text-6xl">
              Privacy Policy
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink" data-testid="privacy-effective-date">
              Effective Date: <span className="text-paper/90">{EFFECTIVE_DATE}</span>
            </p>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink">
              At {firm.name}, we respect the privacy and confidentiality of our clients, prospective clients, visitors,
              and other individuals who interact with us. This Privacy Policy explains how we collect, use, protect, and
              handle personal information in connection with our legal services and website.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sections */}
      <section className="relative pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12">
          <ol className="mx-auto max-w-4xl" data-testid="privacy-sections">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.n} delay={0.05 * i} as="li" className="border-t border-white/10 first:border-t-0">
                <div className="grid gap-6 py-12 md:grid-cols-12">
                  <span
                    aria-hidden="true"
                    className="font-mono text-[11px] tracking-[0.3em] text-gold md:col-span-2"
                  >
                    {s.n}
                  </span>
                  <div className="md:col-span-10">
                    <h2 className="font-serif text-2xl text-paper sm:text-3xl" data-testid={`privacy-section-${s.n}`}>
                      {s.h}
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-ink">{s.b}</p>
                    {s.list && (
                      <ul className="mt-4 space-y-2">
                        {s.list.map((item) => (
                          <li key={item} className="flex gap-3 text-base leading-relaxed text-ink">
                            <span aria-hidden="true" className="mt-2 inline-block h-px w-3 flex-shrink-0 bg-gold" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>

          {/* Contact block */}
          <Reveal className="mx-auto mt-16 max-w-4xl">
            <div className="border border-gold/40 bg-navy-800/60 p-6 md:p-10" data-testid="privacy-contact-block">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Contact</p>
              <p className="mt-4 text-base leading-relaxed text-paper/90">
                For any matter relating to this Privacy Policy, please write to us at{" "}
                <a
                  href={`mailto:${firm.email}`}
                  data-testid="privacy-email-link"
                  className="focus-ring text-gold-light hover:underline"
                >
                  {firm.email}
                </a>{" "}
                or visit the chambers during office hours.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
